import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Business as BusinessIcon,
  ContactMail as ContactIcon,
  Palette as PaletteIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  AutoAwesome as AutoAwesomeIcon,
  Clear as ClearIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Link as LinkIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { CompanyForm } from '../types';
import { CompanyService } from '../services/companyService';
import { StorageService } from '../services/storageService';
import { useTheme } from '../hooks/useTheme';
import CompanyInfoImportModal, { CompanyInfoImportDraft } from '../components/Companies/CompanyInfoImportModal';

const CreateCompanyPage: React.FC = () => {
  const navigate = useNavigate();
  useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [infoImportOpen, setInfoImportOpen] = useState(false);
  const [infoImportDraft, setInfoImportDraft] = useState<CompanyInfoImportDraft | null>(null);
  const [formData, setFormData] = useState<CompanyForm>({
    name: '',
    description: '',
    tagline: '',
    industry: '',
    industryRawText: '',
    specialties: '',
    companySize: '',
    companyType: 'private',
    website: '',
    impressumUrl: '',
    email: '',
    phone: '',
    linkedinUrl: '',
    twitterUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    headquartersAddress: '',
    headquartersCity: '',
    headquartersState: '',
    headquartersCountry: '',
    headquartersPostalCode: '',
    isRemoteFriendly: false,
    remotePolicy: '',
    foundedYear: undefined,
    revenueRange: '',
    employeeRangeText: '',
    employeeCountMin: undefined,
    employeeCountMax: undefined,
    missionStatement: '',
    values: [],
    benefits: [],
    perks: [],
    brandColors: {
      primary: '',
      secondary: '',
    },
  });

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [showAutoFillMessage, setShowAutoFillMessage] = useState(false);

  const handleBack = () => {
    try {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        navigate(-1);
        return;
      }
    } catch {
      // ignore
    }
    navigate('/companies');
  };

  const parseEmployeeRange = (raw?: string) => {
    const input = (raw || '').trim();
    if (!input) return { min: undefined as number | undefined, max: undefined as number | undefined };

    // Normalize common dash variants to a simple hyphen.
    const normalized = input
      .replace(/–/g, '-')
      .replace(/—/g, '-')
      .replace(/−/g, '-')
      .replace(/\s+/g, ' ')
      .trim();

    // Examples:
    // - "5.001–10.000 Beschäftigte"
    // - "5,001-10,000 employees"
    // - "500+ employees"
    const rangeMatch = normalized.match(/(\d[\d.,]*)\s*-\s*(\d[\d.,]*)/);
    if (rangeMatch) {
      const min = Number.parseInt(rangeMatch[1].replace(/[.,]/g, ''), 10);
      const max = Number.parseInt(rangeMatch[2].replace(/[.,]/g, ''), 10);
      return {
        min: Number.isFinite(min) ? min : undefined,
        max: Number.isFinite(max) ? max : undefined,
      };
    }

    const plusMatch = normalized.match(/(\d[\d.,]*)\s*\+/);
    if (plusMatch) {
      const min = Number.parseInt(plusMatch[1].replace(/[.,]/g, ''), 10);
      return {
        min: Number.isFinite(min) ? min : undefined,
        max: undefined,
      };
    }

    return { min: undefined, max: undefined };
  };

  const mapEmployeeRangeToCompanySize = (min?: number, max?: number) => {
    const effectiveMax = typeof max === 'number' ? max : typeof min === 'number' ? min : undefined;
    if (!effectiveMax) return '';
    if (effectiveMax <= 10) return '1-10';
    if (effectiveMax <= 50) return '11-50';
    if (effectiveMax <= 200) return '51-200';
    if (effectiveMax <= 500) return '201-500';
    return '500+';
  };

  const guessIndustryOption = (raw?: string) => {
    const s = (raw || '').toLowerCase();
    if (!s) return '';
    if (/(gesund|krankenhaus|klinik|hospital|medizin|pharma)/i.test(s)) return 'Healthcare';
    if (/(software|it\b|technolog|saas|cloud|ai\b|k\.?i\.?)/i.test(s)) return 'Technology';
    if (/(finanz|bank|versicherung|investment|capital|konto)/i.test(s)) return 'Finance';
    if (/(bildung|schule|univers|education|training)/i.test(s)) return 'Education';
    if (/(einzelhandel|retail|handel|e-commerce|ecommerce)/i.test(s)) return 'Retail';
    if (/(fertig|produktion|herstell|manufactur|industrie)/i.test(s)) return 'Manufacturing';
    if (/(beratung|consulting|agentur|advis)/i.test(s)) return 'Consulting';
    if (/(gemeinn|non\s*-?profit|wohlt|charity|stiftung)/i.test(s)) return 'Non-profit';
    return 'Other';
  };

  const normalizeUrl = (raw: string) => {
    const input = (raw || '').trim();
    if (!input) return '';
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(input)) return input;
    if (input.startsWith('www.')) return `https://${input}`;
    return `https://www.${input}`;
  };

  const handleUrlBlur = (field: keyof CompanyForm) => {
    setFormData(prev => {
      const next = normalizeUrl(String((prev as any)[field] || ''));
      if (next === String((prev as any)[field] || '')) return prev;
      return { ...prev, [field]: next } as any;
    });
  };

  const PHP_API_BASE = (process.env.REACT_APP_PHP_API_BASE || 'http://localhost:8000').replace(/\/$/, '');

  const scrapeCompanyFromUrl = async (value: CompanyInfoImportDraft) => {
    const resp = await fetch(`${PHP_API_BASE}/api/company-scrape.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url: value.url }),
    });

    let payload: any = null;
    try {
      payload = await resp.json();
    } catch {
      // ignore
    }

    if (!resp.ok || !payload?.ok) {
      const msg = payload?.error || `Scrape failed (HTTP ${resp.status})`;
      throw new Error(msg);
    }

    const data = payload.data || {};

    const rangeText = typeof data.employee_range_text === 'string' ? data.employee_range_text : '';
    const parsedRange = parseEmployeeRange(rangeText);

    const rawIndustry = typeof data.industry === 'string' ? data.industry : '';
    const guessedIndustry = guessIndustryOption(rawIndustry);
    const scrapedSpecialties = typeof data.specialties === 'string' ? data.specialties : '';

    const scrapedFoundedYear =
      typeof data.founded_year === 'number'
        ? data.founded_year
        : typeof data.founded_year === 'string'
          ? Number.parseInt(data.founded_year, 10)
          : null;

    setFormData(prev => ({
      ...prev,
      name: prev.name || data.name || '',
      description: prev.description || data.description || '',
      linkedinUrl: prev.linkedinUrl || value.url || '',
      website: prev.website || data.website_url || '',
      tagline: prev.tagline || data.tagline || '',
      industry: prev.industry || guessedIndustry || '',
      industryRawText: prev.industryRawText || rawIndustry || '',
      specialties: prev.specialties || scrapedSpecialties || '',
      companySize:
        prev.companySize ||
        data.company_size ||
        mapEmployeeRangeToCompanySize(parsedRange.min, parsedRange.max) ||
        '',
      foundedYear: prev.foundedYear || (Number.isFinite(scrapedFoundedYear) ? scrapedFoundedYear : undefined),
      employeeRangeText: prev.employeeRangeText || rangeText || '',
      employeeCountMin: prev.employeeCountMin ?? parsedRange.min,
      employeeCountMax: prev.employeeCountMax ?? parsedRange.max,
      headquartersCity: prev.headquartersCity || data.headquarters_city || '',
      headquartersState: prev.headquartersState || data.headquarters_state || '',
      headquartersCountry: prev.headquartersCountry || data.headquarters_country || '',
      impressumUrl: prev.impressumUrl || data.impressum_url || '',
      logoUrl: data.logo_url || prev.logoUrl,
      coverImageUrl: data.cover_image_url || prev.coverImageUrl,
      logo: undefined,
      coverImage: undefined,
    }));

    if (data.logo_url) {
      setLogoPreview(String(data.logo_url));
    }
    if (data.cover_image_url) {
      setCoverPreview(String(data.cover_image_url));
    }
  };

  const formatImportProvider = (provider?: string) => {
    const p = (provider || '').toLowerCase();
    if (!p) return '';
    if (p === 'linkedin') return 'LinkedIn';
    if (p === 'indeed') return 'Indeed';
    if (p === 'xing') return 'XING';
    if (p === 'cylex') return 'Cylex';
    return provider || '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'foundedYear') {
      setFormData(prev => ({ ...prev, [name]: value ? parseInt(value) : undefined }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayInputChange = (field: 'values' | 'benefits' | 'perks', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({ ...prev, [field]: items }));
  };

  const generateSampleData = () => {
    const sampleCompanies = [
      {
        name: 'TechFlow Solutions',
        description: 'TechFlow Solutions is a leading software development company specializing in cloud-native applications, AI-powered solutions, and digital transformation services. We help businesses modernize their technology stack and accelerate their digital journey with innovative, scalable, and secure solutions.',
        tagline: 'Innovating Tomorrow, Today',
        industry: 'Technology',
        companySize: '51-200',
        companyType: 'private' as const,
        website: 'https://www.techflow-solutions.com',
        impressumUrl: 'https://www.techflow-solutions.com/impressum',
        email: 'contact@techflow-solutions.com',
        phone: '+1 (555) 123-4567',
        linkedinUrl: 'https://linkedin.com/company/techflow-solutions',
        twitterUrl: 'https://twitter.com/techflowsolutions',
        facebookUrl: 'https://facebook.com/techflowsolutions',
        instagramUrl: 'https://instagram.com/techflowsolutions',
        headquartersAddress: '123 Innovation Drive, Suite 500',
        headquartersCity: 'San Francisco',
        headquartersState: 'California',
        headquartersCountry: 'United States',
        headquartersPostalCode: '94105',
        isRemoteFriendly: true,
        remotePolicy: 'We offer flexible remote work options with 3 days in office and 2 days remote. Full remote positions available for senior roles. We provide home office stipends and co-working space memberships.',
        foundedYear: 2018,
        revenueRange: '$10M-$50M',
        missionStatement: 'To empower businesses with cutting-edge technology solutions that drive growth, efficiency, and innovation. We believe technology should be accessible, reliable, and transformative for organizations of all sizes.',
        values: ['Innovation', 'Integrity', 'Collaboration', 'Excellence', 'Customer Success', 'Continuous Learning'],
        benefits: ['Health Insurance', 'Dental & Vision', '401k with 6% Match', 'Unlimited PTO', 'Flexible Hours', 'Professional Development Budget', 'Stock Options', 'Parental Leave'],
        perks: ['Free Lunch Fridays', 'Gym Membership', 'Company Retreats', 'Learning Budget', 'Home Office Stipend', 'Pet-Friendly Office', 'Happy Hours', 'Team Building Events'],
        brandColors: { primary: '#2563EB', secondary: '#10B981' },
      },
      {
        name: 'GreenEarth Innovations',
        description: 'GreenEarth Innovations is a sustainable technology company focused on developing renewable energy solutions, smart city technologies, and environmental monitoring systems. We are committed to creating a greener future through innovative technology and sustainable practices.',
        tagline: 'Building a Sustainable Future',
        industry: 'Technology',
        companySize: '11-50',
        companyType: 'startup' as const,
        website: 'https://www.greenearth-innovations.com',
        impressumUrl: 'https://www.greenearth-innovations.com/impressum',
        email: 'hello@greenearth-innovations.com',
        phone: '+1 (555) 987-6543',
        linkedinUrl: 'https://linkedin.com/company/greenearth-innovations',
        twitterUrl: 'https://twitter.com/greenearthtech',
        facebookUrl: 'https://facebook.com/greenearthinnovations',
        instagramUrl: 'https://instagram.com/greenearthtech',
        headquartersAddress: '456 Eco Park Boulevard',
        headquartersCity: 'Portland',
        headquartersState: 'Oregon',
        headquartersCountry: 'United States',
        headquartersPostalCode: '97201',
        isRemoteFriendly: true,
        remotePolicy: 'We are a fully remote company with optional co-working spaces. We provide home office setup budgets and regular team meetups in different cities.',
        foundedYear: 2020,
        revenueRange: '$1M-$10M',
        missionStatement: 'To accelerate the transition to a sustainable future through innovative technology solutions that reduce environmental impact and promote renewable energy adoption.',
        values: ['Sustainability', 'Innovation', 'Transparency', 'Community', 'Environmental Impact', 'Ethical Business'],
        benefits: ['Health Insurance', 'Dental & Vision', '401k with 4% Match', 'Unlimited PTO', 'Fully Remote', 'Learning Budget', 'Wellness Stipend', 'Carbon Offset Program'],
        perks: ['Flexible Schedule', 'Green Office Supplies', 'Electric Vehicle Stipend', 'Environmental Impact Days', 'Sustainable Snacks', 'Team Retreats in Nature', 'Volunteer Time Off', 'Eco-Friendly Swag'],
        brandColors: { primary: '#059669', secondary: '#10B981' },
      },
      {
        name: 'HealthTech Dynamics',
        description: 'HealthTech Dynamics is a healthcare technology company specializing in telemedicine platforms, patient management systems, and AI-powered diagnostic tools. We are revolutionizing healthcare delivery through innovative technology solutions that improve patient outcomes and streamline medical workflows.',
        tagline: 'Transforming Healthcare Through Technology',
        industry: 'Healthcare',
        companySize: '201-500',
        companyType: 'private' as const,
        website: 'https://www.healthtech-dynamics.com',
        impressumUrl: 'https://www.healthtech-dynamics.com/impressum',
        email: 'info@healthtech-dynamics.com',
        phone: '+1 (555) 456-7890',
        linkedinUrl: 'https://linkedin.com/company/healthtech-dynamics',
        twitterUrl: 'https://twitter.com/healthtechdyn',
        facebookUrl: 'https://facebook.com/healthtechdynamics',
        instagramUrl: 'https://instagram.com/healthtechdynamics',
        headquartersAddress: '789 Medical Plaza, Floor 12',
        headquartersCity: 'Boston',
        headquartersState: 'Massachusetts',
        headquartersCountry: 'United States',
        headquartersPostalCode: '02108',
        isRemoteFriendly: false,
        remotePolicy: 'We offer hybrid work with 3 days in office and 2 days remote. Some roles may be fully remote based on department needs.',
        foundedYear: 2015,
        revenueRange: '$50M-$100M',
        missionStatement: 'To improve healthcare outcomes and accessibility through innovative technology solutions that connect patients with quality care and empower healthcare providers with advanced tools.',
        values: ['Patient Care', 'Innovation', 'Compliance', 'Quality', 'Accessibility', 'Continuous Improvement'],
        benefits: ['Health Insurance', 'Dental & Vision', '401k with 8% Match', 'PTO + Sick Days', 'Flexible Hours', 'Professional Development', 'Stock Options', 'Family Leave'],
        perks: ['On-site Gym', 'Healthy Meals', 'Mental Health Support', 'Conference Attendance', 'Research Opportunities', 'Medical Equipment Discounts', 'Team Building', 'Health Screenings'],
        brandColors: { primary: '#DC2626', secondary: '#F59E0B' },
      }
    ];

    // Randomly select a sample company
    const randomIndex = Math.floor(Math.random() * sampleCompanies.length);
    const sampleData = sampleCompanies[randomIndex];
    
    setFormData(sampleData);
    setShowAutoFillMessage(true);
    
    // Hide message after 3 seconds
    setTimeout(() => setShowAutoFillMessage(false), 3000);
  };

  const clearForm = () => {
    setFormData({
      name: '',
      description: '',
      tagline: '',
      industry: '',
      industryRawText: '',
      specialties: '',
      companySize: '',
      companyType: 'private',
      website: '',
      impressumUrl: '',
      email: '',
      phone: '',
      linkedinUrl: '',
      twitterUrl: '',
      facebookUrl: '',
      instagramUrl: '',
      headquartersAddress: '',
      headquartersCity: '',
      headquartersState: '',
      headquartersCountry: '',
      headquartersPostalCode: '',
      isRemoteFriendly: false,
      remotePolicy: '',
      foundedYear: undefined,
      revenueRange: '',
      employeeRangeText: '',
      employeeCountMin: undefined,
      employeeCountMax: undefined,
      missionStatement: '',
      values: [],
      benefits: [],
      perks: [],
      brandColors: {
        primary: '',
        secondary: '',
      },
    });
    setLogoPreview(null);
    setCoverPreview(null);
    setInfoImportDraft(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const company = await CompanyService.createCompany(formData);

      try {
        const updates: Partial<CompanyForm> = {};
        if (formData.logo) {
          updates.logoUrl = await StorageService.uploadCompanyLogo(company.id, formData.logo);
        }
        if (formData.coverImage) {
          updates.coverImageUrl = await StorageService.uploadCompanyCover(company.id, formData.coverImage);
        }
        if (updates.logoUrl || updates.coverImageUrl) {
          await CompanyService.updateCompany(company.id, updates);
        }
      } catch (uploadErr) {
        console.error('Company image upload error:', uploadErr);
        // Do not block navigation if the company was created; show error toast via page error.
        setError(uploadErr instanceof Error ? uploadErr.message : 'Failed to upload company images');
      }

      navigate(`/companies/${company.slug}`);
        } catch (err) {
          console.error('Company creation error:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to create company';
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
  };

  const renderStep1 = () => (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Basic Information
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Tell us about your company's basic details.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <TextField
          fullWidth
          label="Company Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          placeholder="Enter company name"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="Tagline"
          name="tagline"
          value={formData.tagline}
          onChange={handleInputChange}
          placeholder="A short tagline for your company"
          variant="outlined"
        />

        <FormControl fullWidth>
          <InputLabel>Industry</InputLabel>
          <Select
            name="industry"
            value={formData.industry}
            onChange={handleSelectChange}
            label="Industry"
          >
            <MenuItem value="">Select industry</MenuItem>
            <MenuItem value="Technology">Technology</MenuItem>
            <MenuItem value="Healthcare">Healthcare</MenuItem>
            <MenuItem value="Finance">Finance</MenuItem>
            <MenuItem value="Education">Education</MenuItem>
            <MenuItem value="Retail">Retail</MenuItem>
            <MenuItem value="Manufacturing">Manufacturing</MenuItem>
            <MenuItem value="Consulting">Consulting</MenuItem>
            <MenuItem value="Non-profit">Non-profit</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Industry (scraped)"
          name="industryRawText"
          value={formData.industryRawText || ''}
          onChange={handleInputChange}
          placeholder="e.g., Krankenhäuser und Gesundheitseinrichtungen"
          variant="outlined"
          helperText={
            formData.industryRawText
              ? `Auto-selected: ${formData.industry || '—'}`
              : 'This shows the exact “Branche” text fetched from the source.'
          }
        />

        <TextField
          fullWidth
          label="Specialties"
          name="specialties"
          value={formData.specialties || ''}
          onChange={handleInputChange}
          placeholder="e.g., Geriatrie, CSR, Orthopädie, ..."
          variant="outlined"
          multiline
          minRows={2}
        />

        <FormControl fullWidth>
          <InputLabel>Company Size</InputLabel>
          <Select
            name="companySize"
            value={formData.companySize}
            onChange={handleSelectChange}
            label="Company Size"
          >
            <MenuItem value="">Select company size</MenuItem>
            <MenuItem value="1-10">1-10 employees</MenuItem>
            <MenuItem value="11-50">11-50 employees</MenuItem>
            <MenuItem value="51-200">51-200 employees</MenuItem>
            <MenuItem value="201-500">201-500 employees</MenuItem>
            <MenuItem value="500+">500+ employees</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Employees (scraped range)"
          name="employeeRangeText"
          value={formData.employeeRangeText || ''}
          onChange={(e) => {
            const next = e.target.value;
            const parsed = parseEmployeeRange(next);
            setFormData(prev => ({
              ...prev,
              employeeRangeText: next,
              employeeCountMin: parsed.min,
              employeeCountMax: parsed.max,
              companySize: prev.companySize || mapEmployeeRangeToCompanySize(parsed.min, parsed.max) || '',
            }));
          }}
          placeholder="e.g., 5.001–10.000 Beschäftigte"
          variant="outlined"
          helperText={
            formData.employeeCountMin || formData.employeeCountMax
              ? `Parsed: ${formData.employeeCountMin ?? '—'} to ${formData.employeeCountMax ?? '—'}`
              : 'Paste the company size from LinkedIn (e.g. “5.001–10.000 Beschäftigte”)'
          }
        />

        <FormControl fullWidth>
          <InputLabel>Company Type</InputLabel>
          <Select
            name="companyType"
            value={formData.companyType}
            onChange={handleSelectChange}
            label="Company Type"
          >
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="nonprofit">Non-profit</MenuItem>
            <MenuItem value="government">Government</MenuItem>
            <MenuItem value="startup">Startup</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Founded Year"
          name="foundedYear"
          type="number"
          value={formData.foundedYear || ''}
          onChange={handleInputChange}
          placeholder="e.g., 2020"
          inputProps={{
            min: 1800,
            max: new Date().getFullYear(),
          }}
          variant="outlined"
        />
      </Box>

      <TextField
        fullWidth
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleInputChange}
        multiline
        rows={4}
        placeholder="Describe your company, what you do, and what makes you unique..."
        variant="outlined"
      />
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Contact & Location
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Provide contact information and location details.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <TextField
          fullWidth
          label="Website"
          name="website"
          type="url"
          value={formData.website}
          onChange={handleInputChange}
          onBlur={() => handleUrlBlur('website')}
          placeholder="https://example.com"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="Impressum URL"
          name="impressumUrl"
          type="url"
          value={formData.impressumUrl}
          onChange={handleInputChange}
          onBlur={() => handleUrlBlur('impressumUrl')}
          placeholder="https://example.com/impressum"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="contact@company.com"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="Phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="+1 (555) 123-4567"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="LinkedIn URL"
          name="linkedinUrl"
          type="url"
          value={formData.linkedinUrl}
          onChange={handleInputChange}
          onBlur={() => handleUrlBlur('linkedinUrl')}
          placeholder="https://linkedin.com/company/your-company"
          variant="outlined"
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <TextField
          fullWidth
          label="Twitter URL"
          name="twitterUrl"
          type="url"
          value={formData.twitterUrl}
          onChange={handleInputChange}
          onBlur={() => handleUrlBlur('twitterUrl')}
          placeholder="https://twitter.com/your-company"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="Facebook URL"
          name="facebookUrl"
          type="url"
          value={formData.facebookUrl}
          onChange={handleInputChange}
          onBlur={() => handleUrlBlur('facebookUrl')}
          placeholder="https://facebook.com/your-company"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="Instagram URL"
          name="instagramUrl"
          type="url"
          value={formData.instagramUrl}
          onChange={handleInputChange}
          onBlur={() => handleUrlBlur('instagramUrl')}
          placeholder="https://instagram.com/your-company"
          variant="outlined"
        />
      </Box>

      <TextField
        fullWidth
        label="Headquarters Address"
        name="headquartersAddress"
        value={formData.headquartersAddress}
        onChange={handleInputChange}
        multiline
        rows={3}
        placeholder="123 Main Street, Suite 100"
        variant="outlined"
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <TextField
          fullWidth
          label="City"
          name="headquartersCity"
          value={formData.headquartersCity}
          onChange={handleInputChange}
          placeholder="San Francisco"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="State/Province"
          name="headquartersState"
          value={formData.headquartersState}
          onChange={handleInputChange}
          placeholder="California"
          variant="outlined"
        />

        <TextField
          fullWidth
          label="Country"
          name="headquartersCountry"
          value={formData.headquartersCountry}
          onChange={handleInputChange}
          placeholder="United States"
          variant="outlined"
        />
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            name="isRemoteFriendly"
            checked={formData.isRemoteFriendly}
            onChange={handleInputChange}
          />
        }
        label="We are remote-friendly"
        sx={{ mb: formData.isRemoteFriendly ? 2 : 0 }}
      />

      {formData.isRemoteFriendly && (
        <TextField
          fullWidth
          label="Remote Work Policy"
          name="remotePolicy"
          value={formData.remotePolicy}
          onChange={handleInputChange}
          multiline
          rows={3}
          placeholder="Describe your remote work policy..."
          variant="outlined"
        />
      )}
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Company Culture
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Share your company's mission, values, and benefits.
        </Typography>
      </Box>

      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Mission Statement"
          name="missionStatement"
          value={formData.missionStatement}
          onChange={handleInputChange}
          multiline
          rows={4}
          placeholder="What is your company's mission?"
          variant="outlined"
        />

        <Box>
          <TextField
            fullWidth
            label="Company Values"
            value={formData.values?.join(', ') || ''}
            onChange={(e) => handleArrayInputChange('values', e.target.value)}
            multiline
            rows={3}
            placeholder="Innovation, Integrity, Collaboration, Excellence"
            variant="outlined"
            helperText="Enter values separated by commas"
          />
          {formData.values && formData.values.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.values.map((value, index) => (
                <Chip key={index} label={value} size="small" color="primary" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>

        <Box>
          <TextField
            fullWidth
            label="Benefits"
            value={formData.benefits?.join(', ') || ''}
            onChange={(e) => handleArrayInputChange('benefits', e.target.value)}
            multiline
            rows={3}
            placeholder="Health Insurance, 401k, Paid Time Off, Flexible Hours"
            variant="outlined"
            helperText="Enter benefits separated by commas"
          />
          {formData.benefits && formData.benefits.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.benefits.map((benefit, index) => (
                <Chip key={index} label={benefit} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>

        <Box>
          <TextField
            fullWidth
            label="Perks"
            value={formData.perks?.join(', ') || ''}
            onChange={(e) => handleArrayInputChange('perks', e.target.value)}
            multiline
            rows={3}
            placeholder="Free Lunch, Gym Membership, Company Retreats, Learning Budget"
            variant="outlined"
            helperText="Enter perks separated by commas"
          />
          {formData.perks && formData.perks.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.perks.map((perk, index) => (
                <Chip key={index} label={perk} size="small" color="secondary" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );

  const renderStep4 = () => (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Branding & Final Details
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Add your company's visual identity and final details.
        </Typography>
      </Box>

      <Stack spacing={3}>
        <Card
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.06)',
            background: 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Company Images
              </Typography>
              <Chip label="Optional" size="small" variant="outlined" sx={{ borderRadius: 999, fontWeight: 800 }} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2 }}>
              <Box
                sx={{
                  border: '1px dashed rgba(0,0,0,0.18)',
                  borderRadius: 3,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Avatar
                    src={logoPreview || undefined}
                    sx={{ width: 56, height: 56, fontWeight: 900, bgcolor: 'primary.main' }}
                  >
                    {formData.name ? formData.name.charAt(0).toUpperCase() : 'C'}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 900 }}>
                      Logo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Square image (recommended)
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    sx={{ borderRadius: 999, fontWeight: 900, textTransform: 'none', borderWidth: 2 }}
                  >
                    Upload
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setFormData(prev => ({ ...prev, logo: file }));
                        setLogoPreview(URL.createObjectURL(file));
                      }}
                    />
                  </Button>
                  {(logoPreview || formData.logo) && (
                    <IconButton
                      onClick={() => {
                        setFormData(prev => ({ ...prev, logo: undefined, logoUrl: '' } as any));
                        setLogoPreview(null);
                      }}
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 999,
                        border: '1px solid rgba(0,0,0,0.12)',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  border: '1px dashed rgba(0,0,0,0.18)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  minHeight: 140,
                  background: coverPreview
                    ? `url(${coverPreview}) center/cover no-repeat`
                    : 'linear-gradient(135deg, rgba(255,122,24,0.12) 0%, rgba(255,61,0,0.08) 100%)',
                }}
              >
                {!coverPreview && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ fontWeight: 900 }}>
                        Cover / Banner
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Wide image for the company header
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ position: 'absolute', right: 12, bottom: 12, display: 'flex', gap: 1 }}>
                  <Button
                    component="label"
                    variant={coverPreview ? 'contained' : 'outlined'}
                    startIcon={<PhotoCameraIcon />}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 900,
                      textTransform: 'none',
                      borderWidth: coverPreview ? 0 : 2,
                      background: coverPreview ? 'rgba(255,255,255,0.88)' : 'transparent',
                      color: coverPreview ? 'text.primary' : undefined,
                      '&:hover': { background: coverPreview ? 'rgba(255,255,255,0.96)' : undefined },
                    }}
                  >
                    {coverPreview ? 'Replace' : 'Upload'}
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setFormData(prev => ({ ...prev, coverImage: file }));
                        setCoverPreview(URL.createObjectURL(file));
                      }}
                    />
                  </Button>
                  {coverPreview && (
                    <IconButton
                      onClick={() => {
                        setFormData(prev => ({ ...prev, coverImage: undefined, coverImageUrl: '' } as any));
                        setCoverPreview(null);
                      }}
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.88)',
                        '&:hover': { background: 'rgba(255,255,255,0.96)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Primary Brand Color
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <input
                type="color"
                value={formData.brandColors?.primary || '#3B82F6'}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  brandColors: { ...prev.brandColors, primary: e.target.value }
                }))}
                style={{
                  width: 60,
                  height: 40,
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {formData.brandColors?.primary || '#3B82F6'}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Secondary Brand Color
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <input
                type="color"
                value={formData.brandColors?.secondary || '#10B981'}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  brandColors: { ...prev.brandColors, secondary: e.target.value }
                }))}
                style={{
                  width: 60,
                  height: 40,
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {formData.brandColors?.secondary || '#10B981'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <FormControl fullWidth>
          <InputLabel>Revenue Range</InputLabel>
          <Select
            name="revenueRange"
            value={formData.revenueRange}
            onChange={handleSelectChange}
            label="Revenue Range"
          >
            <MenuItem value="">Select revenue range</MenuItem>
            <MenuItem value="<$1M">Less than $1M</MenuItem>
            <MenuItem value="$1M-$10M">$1M - $10M</MenuItem>
            <MenuItem value="$10M-$50M">$10M - $50M</MenuItem>
            <MenuItem value="$50M-$100M">$50M - $100M</MenuItem>
            <MenuItem value="$100M+">$100M+</MenuItem>
          </Select>
        </FormControl>

        <Card
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'primary.200',
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.06) 0%, rgba(16, 185, 129, 0.04) 100%)',
            boxShadow: '0 12px 34px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.9) 0%, rgba(16, 185, 129, 0.85) 100%)',
                    color: 'white',
                    flexShrink: 0,
                  }}
                >
                  <CheckCircleIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                    Review Your Company
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    Quick overview of everything you entered before submitting.
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={currentStep === totalSteps ? 'Ready to submit' : 'Review'}
                color="success"
                variant="outlined"
                sx={{ fontWeight: 800, borderRadius: 999 }}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    Basic
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, textAlign: 'right' }}>{formData.name || 'Not specified'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Tagline</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{formData.tagline || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Industry</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{formData.industry || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Company Size</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{formData.companySize || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Founded</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{formData.foundedYear ? String(formData.foundedYear) : '—'}</Typography>
                  </Box>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Description</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {formData.description || '—'}
                    </Typography>
                  </Box>

                  {infoImportDraft?.url && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                        Import Source
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                          label={formatImportProvider(infoImportDraft.provider) || 'Source'}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 999, fontWeight: 800 }}
                        />
                        <Chip
                          label={infoImportDraft.url}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: 999,
                            maxWidth: 360,
                            '& .MuiChip-label': {
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <ContactIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    Contact & Location
                  </Typography>
                </Box>

                <Stack spacing={1.25}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <LanguageIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>Website</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{formData.website || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <LinkIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>Impressum</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{formData.impressumUrl || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>Email</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{formData.email || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>Phone</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{formData.phone || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <LocationOnIcon color="action" fontSize="small" sx={{ mt: 0.4 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90, mt: 0.25 }}>Location</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formData.headquartersAddress ? `${formData.headquartersAddress}` : ''}
                      {(formData.headquartersCity || formData.headquartersCountry) && (
                        <>
                          {formData.headquartersAddress ? ' • ' : ''}
                          {formData.headquartersCity || ''}
                          {formData.headquartersCountry ? `, ${formData.headquartersCountry}` : ''}
                        </>
                      )}
                      {(!formData.headquartersAddress && !formData.headquartersCity && !formData.headquartersCountry) ? '—' : ''}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 0.5 }}>
                    {formData.linkedinUrl && (
                      <Chip icon={<LinkIcon />} label="LinkedIn" variant="outlined" size="small" sx={{ borderRadius: 999 }} />
                    )}
                    {formData.twitterUrl && (
                      <Chip icon={<LinkIcon />} label="Twitter" variant="outlined" size="small" sx={{ borderRadius: 999 }} />
                    )}
                    {formData.facebookUrl && (
                      <Chip icon={<LinkIcon />} label="Facebook" variant="outlined" size="small" sx={{ borderRadius: 999 }} />
                    )}
                    {formData.instagramUrl && (
                      <Chip icon={<LinkIcon />} label="Instagram" variant="outlined" size="small" sx={{ borderRadius: 999 }} />
                    )}
                    {!formData.linkedinUrl && !formData.twitterUrl && !formData.facebookUrl && !formData.instagramUrl && (
                      <Chip label="No social links" variant="outlined" size="small" sx={{ borderRadius: 999 }} />
                    )}
                  </Box>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <CheckCircleIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    Culture
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Mission</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {formData.missionStatement || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Values</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(formData.values || []).length > 0 ? (
                        formData.values!.slice(0, 10).map((v, idx) => (
                          <Chip key={idx} label={v} size="small" color="primary" variant="outlined" sx={{ borderRadius: 999 }} />
                        ))
                      ) : (
                        <Chip label="—" size="small" variant="outlined" sx={{ borderRadius: 999 }} />
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Benefits</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(formData.benefits || []).length > 0 ? (
                        formData.benefits!.slice(0, 10).map((v, idx) => (
                          <Chip key={idx} label={v} size="small" color="success" variant="outlined" sx={{ borderRadius: 999 }} />
                        ))
                      ) : (
                        <Chip label="—" size="small" variant="outlined" sx={{ borderRadius: 999 }} />
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Perks</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(formData.perks || []).length > 0 ? (
                        formData.perks!.slice(0, 10).map((v, idx) => (
                          <Chip key={idx} label={v} size="small" color="secondary" variant="outlined" sx={{ borderRadius: 999 }} />
                        ))
                      ) : (
                        <Chip label="—" size="small" variant="outlined" sx={{ borderRadius: 999 }} />
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <PaletteIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    Branding
                  </Typography>
                </Box>
                <Stack spacing={1.25}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Primary Color</Typography>
                    <Chip
                      label={formData.brandColors?.primary || '—'}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 800,
                        '& .MuiChip-label': { px: 1.25 },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Secondary Color</Typography>
                    <Chip
                      label={formData.brandColors?.secondary || '—'}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 800,
                        '& .MuiChip-label': { px: 1.25 },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Revenue</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, textAlign: 'right' }}>{formData.revenueRange || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">Remote Friendly</Typography>
                    <Chip
                      label={formData.isRemoteFriendly ? 'Yes' : 'No'}
                      size="small"
                      color={formData.isRemoteFriendly ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ borderRadius: 999, fontWeight: 900 }}
                    />
                  </Box>
                  {formData.isRemoteFriendly && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Remote Policy</Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {formData.remotePolicy || '—'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return renderStep1();
    }
  };

  const steps = [
    { label: 'Basic Information', icon: <BusinessIcon /> },
    { label: 'Contact & Location', icon: <ContactIcon /> },
    { label: 'Company Culture', icon: <CheckCircleIcon /> },
    { label: 'Branding & Final', icon: <PaletteIcon /> },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center', position: 'relative' }}>
          <Box
            sx={{
              position: { xs: 'static', md: 'absolute' },
              left: { md: 0 },
              top: { md: 0 },
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-start' },
              mb: { xs: 2, md: 0 },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 800,
                px: 2.5,
                py: 1,
                borderWidth: 2,
                borderColor: 'rgba(255, 122, 24, 0.35)',
                color: 'text.primary',
                background: 'linear-gradient(135deg, rgba(255, 122, 24, 0.12) 0%, rgba(255, 61, 0, 0.08) 100%)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: 'rgba(255, 61, 0, 0.55)',
                  background: 'linear-gradient(135deg, rgba(255, 122, 24, 0.18) 0%, rgba(255, 61, 0, 0.12) 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 10px 24px rgba(255, 61, 0, 0.12)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Back
            </Button>
          </Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Create Your Company Profile
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Build a comprehensive company profile to attract top talent and showcase your organization
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={generateSampleData}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1
              }}
            >
              Auto Fill Sample Data
            </Button>

            <Button
              variant="outlined"
              startIcon={<PublicIcon />}
              onClick={() => setInfoImportOpen(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                py: 1,
              }}
            >
              Get Info from…
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={clearForm}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1
              }}
            >
              Clear Form
            </Button>
          </Stack>
        </Box>

        <CompanyInfoImportModal
          open={infoImportOpen}
          initialValue={infoImportDraft || undefined}
          onClose={() => setInfoImportOpen(false)}
          onScrape={scrapeCompanyFromUrl}
          onConfirm={(value) => {
            setInfoImportDraft(value);
            setInfoImportOpen(false);
          }}
        />

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Stepper activeStep={currentStep - 1} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: index < currentStep - 1 ? 'primary.main' : 
                                index === currentStep - 1 ? 'primary.main' : 'grey.300',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                      }}
                    >
                      {index < currentStep - 1 ? <CheckCircleIcon /> : step.icon}
                    </Box>
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: index === currentStep - 1 ? 600 : 400 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Step {currentStep} of {totalSteps}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round((currentStep / totalSteps) * 100)}% Complete
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(currentStep / totalSteps) * 100} 
            sx={{ mt: 2, height: 8, borderRadius: 4 }}
          />
        </Paper>

        {/* Form */}
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              {renderCurrentStep()}

              {showAutoFillMessage && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  ✨ Sample data loaded! You can now review and modify the information as needed.
                </Alert>
              )}

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mt: 3,
                    '& .MuiAlert-message': {
                      whiteSpace: 'pre-line',
                      wordBreak: 'break-word'
                    }
                  }}
                >
                  <Typography variant="body2" component="div">
                    {error}
                  </Typography>
                </Alert>
              )}

              <Divider sx={{ my: 4 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                  disabled={currentStep === 1}
                  size="large"
                >
                  Previous
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  endIcon={currentStep === totalSteps ? <CheckCircleIcon /> : <ArrowForwardIcon />}
                  disabled={loading}
                  size="large"
                  sx={{ minWidth: 160 }}
                >
                  {loading ? 'Creating...' : currentStep === totalSteps ? 'Create Company' : 'Next'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CreateCompanyPage;
